import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Label,
  Form,
  Input,
  Button,
  FormFeedback,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import "react-toastify/dist/ReactToastify.css";

import { useProfile } from "../../../Components/Hooks/UserHooks";
import { createSMSSettingKey, updateSMSSettingKeyDetail, viewSMSSettingKeyDetail } from "../_requests";
import BreadCrumb from "../../../Components/Common/BreadCrumb";

const AddEditSMSSetting = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useProfile();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      sms_gateway_key: "",
      sms_gateway_sender: "",
      sms_gateway_content: "",
      sms_gateway_template_id: "",
      type: 1,
    },
    validationSchema: Yup.object({
      sms_gateway_key: Yup.string().required("Gateway Key is required"),
      sms_gateway_sender: Yup.string().required("Sender ID is required"),
      sms_gateway_content: Yup.string().required("Content is required"),
      sms_gateway_template_id: Yup.string().required("Template ID is required"),
      type: Yup.number().required("Type is required"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const data = {
          ...values,
          createdBy: userProfile?.userId,
          updatedBy: userProfile?.userId,
        };

        const res = isEdit 
          ? await updateSMSSettingKeyDetail(id, data) 
          : await createSMSSettingKey(data);

        if (res.status) {
          toast.success(res.message);
          navigate("/settings");
        } else {
          toast.error(res.message || "Failed to save settings");
        }
      } catch (error) {
        console.error("Error saving SMS setting:", error);
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (isEdit) {
      viewSMSSettingKeyDetail(id).then((res: any) => {
        if (res.status && res.data) {
          validation.setValues({
            sms_gateway_key: res.data.sms_gateway_key || "",
            sms_gateway_sender: res.data.sms_gateway_sender || "",
            sms_gateway_content: res.data.sms_gateway_content || "",
            sms_gateway_template_id: res.data.sms_gateway_template_id || "",
            type: res.data.type || 1,
          });
        }
      });
    }
  }, [id, isEdit]);

  document.title = "Paal Dappa | Admin panel";

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <Container fluid>
        <BreadCrumb title={isEdit ? "Edit SMS Setting" : "Add SMS Setting"} pageTitle="Settings" />
        <Row>
          <Col md={12}>
            <Card>
              <CardBody>
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                  }}
                >
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">
                          Type <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="type"
                          type="select"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.type}
                          invalid={validation.touched.type && !!validation.errors.type}
                        >
                          <option value={1}>OTP</option>
                          <option value={2}>ORDER</option>
                        </Input>
                        {validation.touched.type && validation.errors.type ? (
                          <FormFeedback type="invalid">{validation.errors.type}</FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">
                          Sender ID <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="sms_gateway_sender"
                          type="text"
                          placeholder="Enter Sender ID"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.sms_gateway_sender}
                          invalid={validation.touched.sms_gateway_sender && !!validation.errors.sms_gateway_sender}
                        />
                        {validation.touched.sms_gateway_sender && validation.errors.sms_gateway_sender ? (
                          <FormFeedback type="invalid">{validation.errors.sms_gateway_sender}</FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                  </Row>

                  <div className="mb-3">
                    <Label className="form-label">
                      Gateway API Key <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="sms_gateway_key"
                      type="text"
                      placeholder="Enter API Key"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.sms_gateway_key}
                      invalid={validation.touched.sms_gateway_key && !!validation.errors.sms_gateway_key}
                    />
                    {validation.touched.sms_gateway_key && validation.errors.sms_gateway_key ? (
                      <FormFeedback type="invalid">{validation.errors.sms_gateway_key}</FormFeedback>
                    ) : null}
                  </div>

                  <div className="mb-3">
                    <Label className="form-label">
                      Template ID <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="sms_gateway_template_id"
                      type="text"
                      placeholder="Enter Template ID"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.sms_gateway_template_id}
                      invalid={validation.touched.sms_gateway_template_id && !!validation.errors.sms_gateway_template_id}
                    />
                    {validation.touched.sms_gateway_template_id && validation.errors.sms_gateway_template_id ? (
                      <FormFeedback type="invalid">{validation.errors.sms_gateway_template_id}</FormFeedback>
                    ) : null}
                  </div>

                  <div className="mb-4">
                    <Label className="form-label">
                      SMS Content <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="sms_gateway_content"
                      type="textarea"
                      rows="4"
                      placeholder="Enter SMS Content"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.sms_gateway_content}
                      invalid={validation.touched.sms_gateway_content && !!validation.errors.sms_gateway_content}
                    />
                    {validation.touched.sms_gateway_content && validation.errors.sms_gateway_content ? (
                      <FormFeedback type="invalid">{validation.errors.sms_gateway_content}</FormFeedback>
                    ) : null}
                  </div>

                  <div className="text-end">
                    <Button type="button" color="light" className="me-2" onClick={() => navigate("/settings")}>
                      Cancel
                    </Button>
                    <Button type="submit" color="success" disabled={loading}>
                      {loading ? "Saving..." : isEdit ? "Update Setting" : "Add Setting"}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AddEditSMSSetting;
